/**
 * @file Minimal synchronous publish/subscribe bus.
 *
 * The bus is how unrelated systems talk without importing each other: combat
 * emits `damage`, health applies it, audio plays a sound, particles spawn a
 * burst — none of them know the others exist. One instance is created per
 * {@link ../core/Game.js Game} and handed out through the context, so there are
 * no globals.
 */

export class EventBus {
  constructor() {
    /** @type {Map<string, Set<Function>>} */
    this.listeners = new Map();
  }

  /**
   * Subscribes to an event.
   * @param {string} type Event name.
   * @param {(payload: any) => void} handler Callback.
   * @returns {() => void} Unsubscribe function.
   */
  on(type, handler) {
    let handlers = this.listeners.get(type);
    if (!handlers) {
      handlers = new Set();
      this.listeners.set(type, handlers);
    }
    handlers.add(handler);
    return () => this.off(type, handler);
  }

  /**
   * Subscribes to the next occurrence only.
   * @param {string} type Event name.
   * @param {(payload: any) => void} handler Callback.
   * @returns {() => void} Unsubscribe function.
   */
  once(type, handler) {
    const off = this.on(type, (payload) => {
      off();
      handler(payload);
    });
    return off;
  }

  /**
   * Removes a subscription.
   * @param {string} type Event name.
   * @param {(payload: any) => void} handler The exact handler passed to `on`.
   * @returns {void}
   */
  off(type, handler) {
    const handlers = this.listeners.get(type);
    if (!handlers) return;
    handlers.delete(handler);
    if (handlers.size === 0) this.listeners.delete(type);
  }

  /**
   * Emits an event synchronously. Handlers are copied first, so a handler may
   * safely subscribe or unsubscribe during dispatch.
   * @param {string} type Event name.
   * @param {any} [payload] Arbitrary payload; prefer reusing plain objects.
   * @returns {void}
   */
  emit(type, payload) {
    const handlers = this.listeners.get(type);
    if (!handlers || handlers.size === 0) return;
    for (const handler of [...handlers]) handler(payload);
  }

  /**
   * Drops every subscription.
   * @returns {void}
   */
  clear() {
    this.listeners.clear();
  }
}

/**
 * Canonical event names. Using constants keeps typos out of the wiring and
 * gives a single place to discover what the game broadcasts.
 * @readonly
 */
export const Events = Object.freeze({
  /** @type {'entity:created'}  payload: Entity */
  ENTITY_CREATED: 'entity:created',
  /** @type {'entity:destroyed'} payload: Entity */
  ENTITY_DESTROYED: 'entity:destroyed',
  /** payload: { target: Entity, amount: number, source: Entity|null, point: Vector3|null } */
  DAMAGE: 'damage',
  /**
   * Damage that survived every guard {@link Events.DAMAGE} has to clear —
   * immunity filters, shields, i-frames, cheats — reported by `HealthSystem`
   * for anything that reacts to a hit *landing* rather than being requested.
   * A source that wants the hit to shove its target says so with `direction`
   * plus `impulse` (units/second); one that does not omits them. For a bullet
   * the direction is the round's own travel; for melee contact it points from
   * attacker to target.
   * payload: { target: Entity, amount: number, source: Entity|null,
   *            type?: string, direction?: {x,z}|null, impulse?: number }
   * The payload is reused between hits: read it, never keep it.
   */
  DAMAGE_APPLIED: 'damage:applied',
  /** payload: { entity: Entity, source: Entity|null } */
  DIED: 'died',
  /**
   * payload: { entity: Entity } An agent just detected the player — the idle →
   * seek edge, whether it came from line of sight or from being shot.
   */
  ENEMY_AGGRO: 'enemy:aggro',
  /** payload: { position: Vector3, direction: Vector3, owner: Entity } */
  /** payload: { position: Vector3, direction: Vector3, owner: Entity, weapon: Weapon } */
  WEAPON_FIRED: 'weapon:fired',
  /** payload: { position: Vector3, normal: Vector3, target: Entity|null, owner: Entity|null } */
  BULLET_HIT: 'bullet:hit',
  /**
   * A blade was swung. Presentation only, and deliberately not `WEAPON_FIRED`:
   * a swing must not pull in a muzzle flash or a round (issue #122).
   * payload: { position: Vector3, direction: Vector3, range: number,
   *            arcDegrees: number, duration: number, height: number, color: number }
   */
  MELEE_SWING: 'melee:swing',
  /** payload: { entity: Entity } */
  FOOTSTEP: 'footstep',
  /** payload: { entity: Entity } A dash just started (issue: dash SFX). */
  PLAYER_DASHED: 'player:dashed',
  /** payload: { state: string } */
  STATE_CHANGED: 'state:changed',
  /** payload: { score: number, wave: number, kills: number } */
  SCORE_CHANGED: 'score:changed',
  /** Player kill countdown, in seconds. payload: { current: number, max: number } */
  PLAYER_KILLTIMER_CHANGED: 'player:killtimer',

  // --- Phase 3: powerups, waves --------------------------------------------
  /** payload: { entity: Entity, key: string, definition: PowerupDefinition } */
  PICKUP_COLLECTED: 'pickup:collected',
  /** payload: { index: number } A treasure-room chest was opened; hide its visual instance. */
  CHEST_OPENED: 'chest:opened',
  /** payload: { weapon: Weapon | null } `null` means the holder is now unarmed. */
  WEAPON_CHANGED: 'weapon:changed',
  /** payload: { weapon: Weapon, source: Entity } A weapon was thrown away. */
  WEAPON_THROWN: 'weapon:thrown',
  /**
   * Contextual prompt state, re-emitted every frame while the world ticks.
   * payload: { visible: boolean, prompt: string, label: string, position: Vector3 }
   * The position is a live reference into the world: copy it, never keep it.
   */
  INTERACTION_PROMPT: 'interaction:prompt',
  /** payload: { entity: Entity, weapon: Weapon } The gun the player just let go of. */
  WEAPON_DROPPED: 'weapon:dropped',
  /** payload: { effects: StatusEffects } */
  STATUS_CHANGED: 'status:changed',
  /** payload: { position: Vector3, radius: number, color: number } */
  EXPLOSION: 'explosion',

  // --- Issue #5: suicide-bomber hooks --------------------------------------
  /** payload: { entity: Entity, previous: string, next: string } */
  ENEMY_STATE_CHANGED: 'enemy:state',
  /** payload: { entity: Entity, target: Entity } A bomber acquired the player. */
  ENEMY_DETECTED_PLAYER: 'enemy:detected',
  /** payload: { entity: Entity, target: Entity } A bomber began its charge. */
  ENEMY_CHARGE_STARTED: 'enemy:charge',
  /** payload: { entity: Entity, position: Vector3, radius: number, damage: number } */
  ENEMY_EXPLOSION_TRIGGERED: 'enemy:explosion',
  /** payload: { wave: number, count: number } */
  WAVE_STARTED: 'wave:started',
  /** payload: { wave: number } */
  WAVE_CLEARED: 'wave:cleared',
  /** payload: { position: Vector3, duration: number } */
  SPAWN_TELEGRAPH: 'spawn:telegraph',

  // --- Phase 4: presentation ------------------------------------------------
  /**
   * Generic particle request, so any system can emit FX without depending on
   * the particle system.
   * payload: { position: Vector3, direction: Vector3, count: number, options: object }
   */
  FX_BURST: 'fx:burst',
  /** payload: { amount: number } Camera trauma to add, 0..1. */
  SHAKE: 'fx:shake',
  /** payload: { position: Vector3, color: number, intensity: number, life: number } */
  FLASH: 'fx:flash',

  // --- Explosive weapons ----------------------------------------------------
  /** payload: { position: Vector3, direction: Vector3, owner: Entity, rocket: Entity } */
  ROCKET_LAUNCHED: 'rocket:launched',
  /** payload: { position: Vector3, radius: number, damage: number, cause: string, ownerId: number } */
  ROCKET_EXPLODED: 'rocket:exploded',
  /** payload: { position: Vector3, cause: string } Ended without hitting anything. */
  ROCKET_EXPIRED: 'rocket:expired',
  /** payload: { target: Entity, amount: number, distance: number, self: boolean } */
  EXPLOSION_DAMAGE_APPLIED: 'explosion:damage',

  /** payload: { levelIndex: number, seed: number, name: string | null } */
  LEVEL_COMPLETE: 'level:complete',
  /** payload: { levelIndex: number, seed: number, name: string | null, themeKey: string | null } */
  LEVEL_READY: 'level:ready',
  /** payload: { enemy: Entity, player: Entity } */
  ENEMY_CONTACT: 'enemy:contact',
  /** payload: { entity: Entity, target: Entity, position: Vector3, damage: number } */
  ENEMY_MELEE_ATTACK: 'enemy:melee-attack',

  // --- Weapons: ammo, reload, hit reporting ---------------------------------
  // Emitted by the `Weapon` component itself, so UI, audio, animation, stats
  // and AI can all react to one weapon without any of them knowing who holds
  // it. Payloads are reused objects: copy what you keep.
  /** payload: { weapon: Weapon, owner: Entity|null } */
  WEAPON_EQUIPPED: 'weapon:equipped',
  /** payload: { weapon: Weapon, owner: Entity|null } */
  WEAPON_UNEQUIPPED: 'weapon:unequipped',
  /** payload: { weapon: Weapon, current: number, capacity: number, reserve: number } */
  AMMO_CHANGED: 'weapon:ammo',
  /** payload: { weapon: Weapon, owner: Entity|null } Magazine just ran dry. */
  WEAPON_EMPTY: 'weapon:empty',
  /** payload: { weapon: Weapon, owner: Entity|null } Trigger pulled with no ammo. */
  WEAPON_EMPTY_TRIGGER: 'weapon:empty-trigger',
  /** payload: { weapon: Weapon, owner: Entity|null } */
  RELOAD_STARTED: 'weapon:reload-started',
  /**
   * One discrete round/shell/cell/canister entered the magazine during a reload.
   * Fired once per unit transferred, in order, so HUD, audio and animation can
   * present the reload bala-by-bala without inspecting weapon internals.
   * payload: { weapon: Weapon, owner: Entity|null, current: number,
   *   capacity: number, reserve: number, roundIndex: number, roundsTotal: number,
   *   style: string }
   */
  RELOAD_ROUND_LOADED: 'weapon:reload-round-loaded',
  /** payload: { weapon: Weapon, owner: Entity|null, current: number, reserve: number } */
  RELOAD_COMPLETED: 'weapon:reload-completed',
  /**
   * A reload was cut short by a fire request. Single-round reloads commit
   * already-presented rounds; magazine reloads keep only rounds that were
   * already loaded before the reload began. Distinct from RELOAD_CANCELLED
   * (abandoned without a shot attempt) and RELOAD_COMPLETED (full transfer +
   * final clack).
   * payload: { weapon: Weapon, owner: Entity|null, current: number, reserve: number }
   */
  RELOAD_INTERRUPTED: 'weapon:reload-interrupted',
  /** payload: { weapon: Weapon, owner: Entity|null } */
  RELOAD_CANCELLED: 'weapon:reload-cancelled',
  /** payload: { weapon: Weapon, target: Entity, position: Vector3, damage: number } */
  WEAPON_HIT: 'weapon:hit',
  /** payload: { weapon: Weapon, owner: Entity|null } Hitscan shot that hit nothing. */
  WEAPON_MISSED: 'weapon:missed',
  /** payload: { weapon: Weapon } */
  WEAPON_ENABLED: 'weapon:enabled',
  /** payload: { weapon: Weapon } */
  WEAPON_DISABLED: 'weapon:disabled',
  /** payload: { entity: Entity, weapon: Weapon } A flame stream began. */
  FLAME_START: 'flame:start',
  /** payload: { entity: Entity | null } The flame stream stopped. */
  FLAME_END: 'flame:end',
  /** payload: { entity: Entity, burn: Burn } */
  BURN_APPLIED: 'burn:applied',
  /** payload: { entity: Entity } */
  BURN_EXPIRED: 'burn:expired',

  /**
   * Edge-triggered: emitted only when one of these booleans actually changes,
   * not every frame. `MusicCueSystem` is the sole emitter; `MusicDirector` is
   * the sole listener — this is the entire contract between "what is
   * happening in the match" and "what the layered music does about it".
   * payload: { scratch: boolean, synth: boolean, kick: boolean, bass2: boolean, pulse: boolean }
   */
  MUSIC_CUE_CHANGED: 'music:cue',

  /** payload: { visible: boolean } Shows/hides the "quit to menu?" popup. */
  CONFIRM_QUIT_PROMPT: 'confirm-quit:prompt',
  /** payload: { confirmed: boolean } A button in the popup was clicked. */
  CONFIRM_QUIT_RESPONSE: 'confirm-quit:response',

  /**
   * A nav-list row in the menu/pause overlay was clicked. The HUD only
   * reports which one; whichever state is active (`MenuState`, `PausedState`)
   * decides what the action id means, same contract as `CONFIRM_QUIT_RESPONSE`.
   * payload: { action: 'play' | 'resume' | 'mainMenu' }
   */
  MENU_NAV_ACTION: 'menu-nav:action',

  // --- enemy_ranged_01 hooks -------------------------------------------------
  /** payload: { entity: Entity, player: Entity } */
  ENEMY_PLAYER_DETECTED: 'enemy:player-detected',
  /** payload: { entity: Entity, target: Entity|null } */
  ENEMY_TARGET_LOST: 'enemy:target-lost',
  /** payload: { entity: Entity, source: Entity|null, amount: number } */
  ENEMY_DAMAGE_RECEIVED: 'enemy:damage-received',
  /** payload: { entity: Entity, target: Entity|null } */
  ENEMY_ATTACK_STARTED: 'enemy:attack-started',
  /** payload: { entity: Entity, weapon: Weapon|null, target: Entity|null } */
  ENEMY_WEAPON_FIRED: 'enemy:weapon-fired',
  /** payload: { entity: Entity, target: Entity|null } */
  ENEMY_SHOT_HIT: 'enemy:shot-hit',
  /** payload: { entity: Entity, target: Entity|null } */
  ENEMY_SHOT_MISSED: 'enemy:shot-missed',
  /** payload: { entity: Entity, destination: { x: number, z: number } } */
  ENEMY_REPOSITION_STARTED: 'enemy:reposition-started',
  /** payload: { entity: Entity, previousPattern: string, newPattern: string } */
  ENEMY_PATTERN_CHANGED: 'enemy:pattern-changed',
  /** payload: { entity: Entity } */
  ENEMY_DEATH: 'enemy:death',
  /**
   * Generic HUD toast request, so any system can surface micro-feedback without
   * depending on the HUD.
   * payload: { text: string, type?: string, duration?: number }
   */
  NOTIFY_ADD: 'notify:add',

  // --- Player-facing settings and tooling ----------------------------------
  // Reported rather than read: the analytics bridge is the only listener, and
  // it must not have to reach into the HUD's private sliders to see a change.
  /** payload: { setting: string, value: number | string | boolean } */
  SETTINGS_CHANGED: 'settings:changed',
  /** payload: { open: boolean } The developer window was toggled. */
  DEBUG_TOGGLED: 'debug:toggled'
});
